import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, ParkingCircle, DoorOpen, MapPin, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Waypoint, WaypointType } from '../lib/types';

interface WaypointItemProps {
  waypoint: Waypoint;
  index: number;
  onRemove: (id: string) => void;
}

function getWaypointIcon(type?: WaypointType) {
  switch (type) {
    case 'P':
      return <ParkingCircle className="w-4 h-4 text-blue-500" />;
    case 'E':
      return <DoorOpen className="w-4 h-4 text-green-500" />;
    case 'G':
      return <Navigation className="w-4 h-4 text-purple-500" />;
    case 'A':
    default:
      return <MapPin className="w-4 h-4 text-gray-500" />;
  }
}

export function WaypointItem({ waypoint, index, onRemove }: WaypointItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: waypoint.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-3 bg-card border rounded-lg',
        isDragging && 'opacity-50 shadow-lg z-50'
      )}
    >
      {/* Drag handle */}
      <button
        className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Index number */}
      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center flex-shrink-0">
        {index + 1}
      </span>

      {/* Icon */}
      <span className="flex-shrink-0">{getWaypointIcon(waypoint.type)}</span>

      {/* Label */}
      <span className="flex-1 text-sm truncate">{waypoint.label}</span>

      {/* Remove button */}
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 text-muted-foreground hover:text-destructive flex-shrink-0"
        onClick={() => onRemove(waypoint.id)}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
